import * as XLSX from 'xlsx';
import i18n from '../i18n';

/**
 * Export an array of objects to a real .xlsx file.
 * @param {Array} data - Array of row objects
 * @param {Array} columns - Array of column descriptors: { key, label }
 * @param {string} filename - base filename (without extension)
 */
export const exportToXlsx = (data = [], columns = [], filename = 'export') => {
  if (!data || !data.length) {
    console.warn('No data to export');
    return false;
  }

  // Helper to detect id-like keys and avoid exporting them
  const isIdKey = (key) => {
    if (!key && key !== 0) return false;
    const s = String(key);
    const lower = s.toLowerCase();

    // Exact id
    if (lower === 'id') return true;

    // snake_case id (ends with _id)
    if (lower.endsWith('_id')) return true;

    // camelCase id (ends with Id) - check capital I to avoid words like 'paid'
    if (s.length > 2 && s.endsWith('Id')) return true;

    return false;
  };

  // Filter out any id-like columns from the provided columns list
  let effectiveColumns = Array.isArray(columns) && columns.length > 0 ? columns.slice() : [];

  if (!effectiveColumns || effectiveColumns.length === 0) {
    // Infer columns from first row (preserve order)
    const first = data[0] || {};
    effectiveColumns = Object.keys(first).map((k) => ({ key: k, label: k }));
  }

  // Remove id-like columns (id, *_id, camelCase *Id)
  effectiveColumns = effectiveColumns.filter((c) => !isIdKey(c.key));

  // Build header row and rows array
  const headers = (effectiveColumns || []).map((c) => c.label || c.key || '');
  const keys = (effectiveColumns || []).map((c) => c.key || '');

  const rows = data.map((row) => keys.map((k) => (row[k] === null || row[k] === undefined ? '' : row[k])));

  const aoa = [headers, ...rows];

  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);

  return true;
};

// Auto-size worksheet columns to their longest cell so the Instructions sheet
// is readable without manual resizing.
const autoFitColumns = (aoa) => {
  const widths = [];
  (aoa || []).forEach((row) => {
    (row || []).forEach((cell, i) => {
      const len = cell == null ? 0 : String(cell).length;
      widths[i] = Math.max(widths[i] || 10, len + 2);
    });
  });
  return widths.map((w) => ({ wch: Math.min(w, 60) }));
};

/**
 * Download a template .xlsx file with a header row (no data rows), and an
 * optional second "Instructions" sheet describing each column.
 *
 * @param {Array<string>} templateHeaders - array of header strings (Sheet1 row 1)
 * @param {string} filename - base filename without extension
 * @param {Object} [options]
 * @param {Object<string, {required?: boolean, allowed?: string[], note?: string}>} [options.columnSpecs]
 *   Map of header -> spec. When provided, an "Instructions" sheet is added listing
 *   which columns are required and which accept fixed/enum values. The header row
 *   on Sheet1 is left untouched so files filled from this template import cleanly.
 */
export const downloadTemplateXlsx = (templateHeaders = [], filename = 'template', options = {}) => {
  const { columnSpecs = null } = options;

  // Filter out id-like headers if headers appear to be keys
  const isIdKeyFromHeader = (h) => {
    if (!h && h !== 0) return false;
    const s = String(h);
    const lower = s.toLowerCase();
    if (lower === 'id') return true;
    if (lower.endsWith('_id')) return true;
    if (s.length > 2 && s.endsWith('Id')) return true;
    return false;
  };

  const headers = (templateHeaders || []).filter((h) => !isIdKeyFromHeader(h)).map((h) => h || '');
  const ws = XLSX.utils.aoa_to_sheet([headers]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

  // Optional Instructions sheet describing required + enum/fixed-value columns.
  if (columnSpecs && typeof columnSpecs === 'object') {
    const instrAoa = [
      [i18n.t('importTemplate.heading')],
      [i18n.t('importTemplate.intro1')],
      [i18n.t('importTemplate.intro2')],
      [i18n.t('importTemplate.intro3')],
      [],
      [i18n.t('importTemplate.columns.column'), i18n.t('importTemplate.columns.required'), i18n.t('importTemplate.columns.allowedValues'), i18n.t('importTemplate.columns.notes')],
      ...headers.map((h) => {
        const spec = columnSpecs[h] || {};
        return [
          h,
          spec.required ? i18n.t('importTemplate.yes') : i18n.t('importTemplate.no'),
          Array.isArray(spec.allowed) && spec.allowed.length ? spec.allowed.join(' | ') : '',
          spec.note || '',
        ];
      }),
    ];
    const instrWs = XLSX.utils.aoa_to_sheet(instrAoa);
    instrWs['!cols'] = autoFitColumns(instrAoa);
    XLSX.utils.book_append_sheet(wb, instrWs, i18n.t('importTemplate.sheetName'));
  }

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/octet-stream' });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

/** Convenience wrapper for exporting users (mirrors previous CSV helper) */
export const exportUsersToXlsx = (users, filename = 'users_export') => {
  if (!users || users.length === 0) return false;
  // If callers expect CSV-style column metadata, they should pass columns explicitly.
  // For compatibility, try to infer columns from first user object.
  const first = users[0];
  const keys = Object.keys(first || {});
  // Filter out id-like keys
  const filteredKeys = keys.filter((k) => {
    if (!k && k !== 0) return false;
    const s = String(k);
    const lower = s.toLowerCase();
    if (lower === 'id') return false;
    if (lower.endsWith('_id')) return false;
    if (s.length > 2 && s.endsWith('Id')) return false;
    return true;
  });

  const columns = filteredKeys.map((k) => ({ key: k, label: k.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()) }));
  return exportToXlsx(users, columns, filename);
};

const xlsxExportUtils = {
  exportToXlsx,
  downloadTemplateXlsx,
  exportUsersToXlsx,
};

export default xlsxExportUtils;
